import { useCallback, useEffect, useState } from 'react'
import { ComboboxOption } from 'modules/ui/molecules/Combobox'
import {
  AmplySearchData,
  AmplyCompanyData,
  DoubleTheDonationSearchData,
  DoubleTheDonationCompany,
} from 'pages/api/employer-match/amply'
import {
  useSearchEmployerMatchCompaniesQuery,
  EmployerMatchTypes,
} from 'redux/services/employer-matching'

export type EmployerMatchingCompany = {
  id: number | string
  company_name: string
  subsidiaries?: string[]
  highlight?: {
    company_name?: string | string[]
    linkedin_names?: string[]
    subsidiaries?: string[]
  }
}

export type EmployerMatchingProvider = 'DOUBLE_THE_DONATION' | 'AMPLY'

interface UseEmployerMatchingSearchOptions {
  provider: EmployerMatchingProvider
  query: string
  enabled?: boolean
}

/**
 * Transforms Double the Donation data to <option> attributes format
 * @param companyData - The company data to map
 * @returns The mapped data
 */
const getDoubleTheDonationMappedData = (companyData: EmployerMatchingCompany): ComboboxOption => {
  const highlight = companyData.highlight
  const toAppendCompanyName = ` (${companyData.company_name})`

  const highlightName =
    highlight?.company_name && highlight.company_name.length > 0
      ? highlight.company_name[0]
      : highlight?.linkedin_names && highlight.linkedin_names.length > 0
      ? highlight.linkedin_names[0] + toAppendCompanyName
      : highlight?.subsidiaries && highlight.subsidiaries.length > 0
      ? highlight.subsidiaries[0] + toAppendCompanyName
      : companyData.company_name

  // Strip HTML tags from highlight results
  const span = document.createElement('span')
  span.innerHTML = highlightName
  const cleanLabel = span.textContent || span.innerText || companyData.company_name

  return {
    label: cleanLabel,
    value: companyData.id,
  }
}

/**
 * Transforms Amply data to <option> attributes format
 * @param companyData - The Amply company data to map
 * @returns The mapped data
 */
const getAmplyMappedData = (companyData: AmplyCompanyData): ComboboxOption => {
  return {
    label: companyData.name,
    value: `amply_${companyData.company_id}`,
  }
}

const useEmployerMatchingSearch = ({
  provider,
  query,
  enabled = true,
}: UseEmployerMatchingSearchOptions) => {
  const [debouncedQuery, setDebouncedQuery] = useState('')

  // Debounce the query input
  useEffect(() => {
    // Clear immediately if query is empty
    if (!query || query.trim() === '') {
      setDebouncedQuery('')
      return
    }

    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim())
    }, 300) // 300ms debounce

    return () => clearTimeout(timer)
  }, [query])

  // Map provider to EmployerMatchTypes
  const employerMatchType =
    provider === 'DOUBLE_THE_DONATION'
      ? EmployerMatchTypes.DOUBLE_THE_DONATION
      : EmployerMatchTypes.AMPLY

  // Skip RTK Query if query is too short, disabled, or no query provided
  const shouldSkip = !debouncedQuery || !enabled || debouncedQuery.length < 3

  // Use RTK Query for data fetching with debounced query
  const {
    data: queryData,
    error: queryError,
    isLoading,
    isFetching,
  } = useSearchEmployerMatchCompaniesQuery(
    {
      companySearchString: debouncedQuery,
      type: employerMatchType,
    },
    {
      skip: shouldSkip,
    },
  )

  // Process the data based on provider type
  const processedData = useCallback(() => {
    if (!queryData) {
      return { data: [], companies: [] }
    }

    if (provider === 'DOUBLE_THE_DONATION') {
      // Handle DTD data (array of DoubleTheDonationCompany)
      const dtdCompanies = queryData as DoubleTheDonationSearchData

      // Filter out companies with invalid data before processing
      const validCompanies = dtdCompanies.filter((dtdCompany: DoubleTheDonationCompany) => {
        return (
          dtdCompany.id != null && // Not null or undefined
          dtdCompany.company_name.trim() !== '' // Name is not just whitespace
        )
      })

      // Map to EmployerMatchingCompany format
      const mappedCompanies = validCompanies.map((dtdCompany: DoubleTheDonationCompany) => ({
        id: dtdCompany.id,
        company_name: dtdCompany.company_name,
        subsidiaries: dtdCompany.subsidiaries,
        highlight: dtdCompany.highlight,
      }))

      // Map to ComboboxOption format
      const mappedData = mappedCompanies.map((company: EmployerMatchingCompany) =>
        getDoubleTheDonationMappedData(company),
      )

      return { data: mappedData, companies: mappedCompanies }
    } else {
      // Handle Amply data (AmplySearchData structure)
      const amplyData = queryData as AmplySearchData
      const companies = amplyData.names || []

      // Filter out companies with invalid data before processing
      const validCompanies = companies.filter((companyData: AmplyCompanyData) => {
        return (
          companyData.company_id != null && // Not null or undefined
          companyData.name.trim() // Name is not just whitespace
        )
      })

      // Map to EmployerMatchingCompany format
      const mappedCompanies = validCompanies.map((companyData: AmplyCompanyData) => ({
        id: companyData.company_id,
        company_name: companyData.name,
      }))

      // Map to ComboboxOption format
      const mappedData = validCompanies.map((companyData: AmplyCompanyData) =>
        getAmplyMappedData(companyData),
      )

      return { data: mappedData, companies: mappedCompanies }
    }
  }, [queryData, provider])

  const { data, companies } = processedData()

  return {
    data,
    companies,
    error: !!queryError,
    loading: isLoading || isFetching,
    hasMore: false,
  }
}

export default useEmployerMatchingSearch
